/**
 * DataFreshnessWarning Component
 *
 * Displays an orange warning banner when report data appears to be missing
 * or stale (e.g. external cron/ETL hasn't run yet).
 *
 * Detection: compares expected dates in the requested range against
 * actual dates present in the daily data array.
 */

// Local calendar dates: toISOString() is UTC, which moves local midnight to
// the previous day for anyone east of Greenwich.
import { format } from 'date-fns'
import { useMemo } from 'react'
import { AlertTriangle } from 'lucide-react'

/**
 * Build a Set of YYYY-MM-DD strings for every day in [from, to].
 */
function buildExpectedDates(from, to) {
  const dates = new Set()
  const current = new Date(from)
  current.setHours(0, 0, 0, 0)
  const end = new Date(to)
  end.setHours(0, 0, 0, 0)

  while (current <= end) {
    dates.add(format(current, 'yyyy-MM-dd'))
    current.setDate(current.getDate() + 1)
  }
  return dates
}

/**
 * Normalise a date value (string or Date) to YYYY-MM-DD.
 */
function toDateStr(value) {
  if (!value) return null
  if (typeof value === 'string') return value.slice(0, 10)
  return format(value, 'yyyy-MM-dd')
}

/**
 * Analyse freshness of daily report data.
 *
 * @param {Array}  daily      – array of daily data objects
 * @param {string} dateField  – key containing the date (e.g. 'date', 'data_date')
 * @param {Date}   rangeFrom  – requested start date
 * @param {Date}   rangeTo    – requested end date
 * @returns {{ missingDays: number, totalDays: number, latestDate: string|null, isStale: boolean }}
 */
function analyseFreshness(daily, dateField, rangeFrom, rangeTo) {
  const expected = buildExpectedDates(rangeFrom, rangeTo)
  const totalDays = expected.size

  if (!daily || daily.length === 0) {
    return { missingDays: totalDays, totalDays, latestDate: null, isStale: true }
  }

  // Collect actual dates present
  const actual = new Set()
  let latestDate = null
  for (const row of daily) {
    const d = toDateStr(row[dateField])
    if (d) {
      actual.add(d)
      if (!latestDate || d > latestDate) latestDate = d
    }
  }

  // Today's data is typically not yet available (ETL runs at end of day),
  // so exclude today from "missing" count to avoid false positives.
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const todayStr = format(today, 'yyyy-MM-dd')

  let missingDays = 0
  for (const d of expected) {
    if (d === todayStr) continue // today is expected to be empty
    if (!actual.has(d)) missingDays++
  }

  const effectiveTotalDays = expected.has(todayStr) ? totalDays - 1 : totalDays

  // Check if the range includes recent dates and data is behind
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)
  const yesterdayStr = format(yesterday, 'yyyy-MM-dd')

  const rangeToStr = toDateStr(rangeTo)
  const rangeIncludesRecent = rangeToStr >= yesterdayStr

  // "stale" = latest data is more than 1 day behind (yesterday missing)
  const isStale = latestDate && rangeToStr && latestDate < yesterdayStr && rangeIncludesRecent

  return { missingDays, totalDays: effectiveTotalDays, latestDate, isStale }
}

/**
 * DataFreshnessWarning
 *
 * Props:
 *   daily     – the daily data array from the API
 *   dateField – which key holds the date string (default: 'date')
 *   dateRange – { from: Date, to: Date }
 *   loading   – suppress while loading
 *   className – optional extra classes
 */
export default function DataFreshnessWarning({
  daily,
  dateField = 'date',
  dateRange,
  loading = false,
  className = '',
}) {
  const freshness = useMemo(() => {
    if (!daily || !dateRange?.from || !dateRange?.to) return null
    return analyseFreshness(daily, dateField, dateRange.from, dateRange.to)
  }, [daily, dateField, dateRange])

  // Don't render while loading, or if there's no issue
  if (loading || !freshness) return null

  // Only warn if ≥2 days are missing (excluding today) OR data is stale
  const significantGap = freshness.missingDays >= 2
  const shouldWarn = significantGap || freshness.isStale

  if (!shouldWarn) return null

  // Build a human-readable message
  const parts = []

  if (freshness.isStale && freshness.latestDate) {
    parts.push(`Latest data is from ${formatDateReadable(freshness.latestDate)}`)
  }

  if (significantGap && freshness.totalDays > 0) {
    const pct = Math.round(((freshness.totalDays - freshness.missingDays) / freshness.totalDays) * 100)
    parts.push(`${freshness.missingDays} of ${freshness.totalDays} days missing (${pct}% coverage)`)
  }

  if (parts.length === 0) return null

  return (
    <div
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${className}`}
      style={{
        backgroundColor: 'rgba(245, 158, 11, 0.08)',
        borderColor: 'rgba(245, 158, 11, 0.3)',
        color: 'inherit',
      }}
    >
      <AlertTriangle
        className="h-4 w-4 shrink-0 mt-0.5"
        style={{ color: '#F59E0B' }}
      />
      <div>
        <span className="font-medium" style={{ color: '#D97706' }}>
          Incomplete data
        </span>
        <span className="text-muted-foreground">
          {' '}&mdash; {parts.join('. ')}. This may indicate the sync hasn&apos;t run yet.
        </span>
      </div>
    </div>
  )
}

function formatDateReadable(dateStr) {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
